import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container, Card, CardContent, Grid, IconButton, Divider } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import StateContext from '../../Contexts/StateContext';
import Axios from '../../AxiosInstance';
import config, { environment } from '../../config';

export default function FamilyTreeHome() {
    const { t, i18n } = useTranslation();
    const globalState = useContext(StateContext);
    const navigate = useNavigate();
    const [savedTrees, setSavedTrees] = useState([]);

    useEffect(() => {
        const fetchTrees = async () => {
            const trees = [];
            // 1. Get from localStorage if logged in
            if (globalState.userIsLogged) {
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith('family_tree_')) {
                        try {
                            const rawData = localStorage.getItem(key);
                            if (rawData) {
                                const data = JSON.parse(rawData);
                                const id = key.replace('family_tree_', '');
                                trees.push({
                                    id: id,
                                    title: data.title || t('Thematic.FamilyTree.Title'),
                                    membersCount: data.members?.length || 0,
                                    token: data.token || 'secret',
                                    source: 'local'
                                });
                            }
                        } catch (e) {
                            console.error("Error parsing saved tree", e);
                        }
                    }
                }
            }

            // 2. Get from Database if logged in
            if (globalState.userIsLogged) {
                try {
                    const response = await Axios.get(config[environment].familyTreeEndpoint);
                    if (response.data && Array.isArray(response.data)) {
                        response.data.forEach(dbTree => {
                            // Csak akkor adjuk hozzá, ha mi vagyunk a tulajdonosok vagy nincs megadva tulajdonos (biztonsági szűrés)
                            const isOwner = !dbTree.user || dbTree.user === globalState.userUsername || dbTree.is_owner;
                            
                            // Kiszűrjük a duplikációkat ID vagy TOKEN alapján
                            const isDuplicate = trees.some(t => 
                                String(t.id) === String(dbTree.id) || 
                                (t.token !== 'secret' && t.token === dbTree.token)
                            );

                            if (isOwner && !isDuplicate) {
                                trees.push({
                                    id: dbTree.id,
                                    title: dbTree.title || t('Thematic.FamilyTree.Title'),
                                    membersCount: dbTree.members?.length || 0,
                                    token: dbTree.token,
                                    source: 'db',
                                    updatedAt: dbTree.updated_at,
                                    version: dbTree.version
                                });
                            }
                        });
                    }
                } catch (error) {
                    console.error("Error fetching trees from DB", error);
                }
            }
            setSavedTrees(trees);
        };

        fetchTrees();
    }, [t, globalState.userIsLogged]);

    const handleCreateNew = () => {
        if (!globalState.userIsLogged) {
            alert(t('Thematic.FamilyTree.LoginRequired'));
            return;
        }
        const randomId = Math.random().toString(36).substr(2, 9);
        const secretToken = Math.random().toString(36).substr(2, 15);
        
        // Elmentjük az alap adatokat a tokennel együtt, hogy később is elérjük
        localStorage.setItem(`family_tree_${randomId}`, JSON.stringify({
            members: [],
            connections: [],
            title: '',
            token: secretToken
        }));

        navigate(`/${i18n.language}/csaladfaallitas/${randomId}/${secretToken}`);
    };

    const handleEdit = (tree) => {
        navigate(`/${i18n.language}/csaladfaallitas/${tree.id}/${tree.token}`);
    };

    const handleDelete = async (id, token, source) => {
        if (window.confirm(t('Common.Delete') + "?")) {
            if (source === 'db' && globalState.userIsLogged) {
                try {
                    await Axios.delete(`${config[environment].familyTreeEndpoint}${token}/`);
                } catch (error) {
                    console.error("Error deleting tree from DB", error);
                    alert(t('Thematic.FamilyTree.SaveError'));
                    return;
                }
            }
            localStorage.removeItem(`family_tree_${id}`);
            setSavedTrees(savedTrees.filter(t => t.id !== id));
        }
    };

    return (
        <Container maxWidth="md" sx={{ mt: { xs: '20px', md: '50px', lg: '100px' }, pb: 10 }}>
            <Box textAlign="center" py={5}>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {t('Thematic.FamilyTree.Title')}
                </Typography>
                <Typography variant="body1" paragraph color="textSecondary">
                    {t('Thematic.FamilyTree.PrivateNote')}
                </Typography>
                <Button 
                    variant="contained" 
                    color="primary" 
                    size="large" 
                    onClick={handleCreateNew}
                    sx={{ mt: 2, px: 4, py: 1.5, borderRadius: 2 }}
                >
                    {t('Thematic.FamilyTree.CreateNew')}
                </Button>
            </Box>

            <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2, borderLeft: '4px solid', borderColor: 'primary.main' }}>
                <Typography variant="h6" gutterBottom color="primary">
                    {t('Thematic.FamilyTree.InfoTooltip').split('\n')[0]}
                </Typography>
                <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-line' }}>
                    {t('Thematic.FamilyTree.InfoTooltip').split('\n').slice(1).join('\n')}
                </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                {t('Thematic.FamilyTree.SavedTrees')}
            </Typography>

            {savedTrees.length === 0 ? (
                <Typography variant="body1" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    {t('Thematic.FamilyTree.NoSavedTrees')}
                </Typography>
            ) : (
                <Grid container spacing={3}>
                    {savedTrees.map((tree) => (
                        <Grid item xs={12} sm={6} key={tree.id}>
                            <Card variant="outlined" sx={{ 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                transition: '0.3s',
                                '&:hover': { boxShadow: 3, borderColor: 'primary.main' }
                            }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="h6" component="div" sx={{ fontWeight: 'medium' }}>
                                                {tree.title || t('Thematic.FamilyTree.Title')}
                                            </Typography>
                                            <Box display="flex" gap={1}>
                                                <Typography variant="caption" display="block" color="text.disabled" sx={{ mb: 1 }}>
                                                    ID: {tree.id}
                                                </Typography>
                                                {tree.source === 'local' && (
                                                    <Typography variant="caption" display="block" color="warning.main" sx={{ fontStyle: 'italic' }}>
                                                        ({t('Thematic.FamilyTree.LocalOnly')})
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography sx={{ mb: 1.5 }} color="text.secondary">
                                                {tree.membersCount} {t('Thematic.FamilyTree.Name').toLowerCase()}
                                            </Typography>
                                            {tree.updatedAt && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {t('Thematic.FamilyTree.LastUpdate')}: {new Date(tree.updatedAt).toLocaleString(i18n.language)}
                                                </Typography>
                                            )}
                                            {tree.version && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {t('Thematic.FamilyTree.Version')}: {tree.version}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box>
                                            <IconButton size="small" color="primary" onClick={() => handleEdit(tree)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(tree.id, tree.token, tree.source)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Button 
                                        variant="outlined" 
                                        fullWidth 
                                        sx={{ mt: 1 }}
                                        onClick={() => handleEdit(tree)}
                                    >
                                        {t('Thematic.FamilyTree.EditTree')}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Container>
    );
}
